// Background body prefetcher. The list cache holds metadata for 150 mails
// per folder; this module fills in their bodies so opening any of those
// mails offline shows the full message instead of an "uncached" state.
//
// Design constraints:
//   • At most MAX_CONCURRENT outstanding /v1/messages/<uid> requests so the
//     mail-api isn't drowned and the user's in-flight clicks stay snappy.
//   • Skip uids whose body is already in IDB.
//   • Pause when offline or the tab is hidden; resume automatically when
//     either condition flips back.
//   • Dedupe within a session via the `seen` Set so a 60s poll doesn't
//     keep re-queueing the same uids.

import { api } from "../api.js";
import { getCachedBody, putCachedBody } from "./store.js";
import { network } from "./net.js";

// The backend serialises IMAP per user, so a prefetch in flight blocks the
// user's interactive folder/message loads behind it. Keep concurrency at 1
// and trickle with a delay between fetches so interactive requests always
// slot in quickly. Offline body caching is a nicety, never worth a slow inbox.
const MAX_CONCURRENT = 1;
const PREFETCH_DELAY_MS = 1200;

const queue = [];          // [{ slot, uid, folder }, ...]
const seen  = new Set();   // session-level dedupe: `${slot}:${uid}`
let   active = 0;
// Interactive requests bump this; the prefetcher waits until the user has
// been idle for a moment so it never competes with a click.
let   lastInteractiveAt = 0;
export function noteInteractive() { lastInteractiveAt = Date.now(); }

/**
 * Queue a batch of uids for body prefetch.
 *   slot:   account slot (Number)
 *   uids:   iterable of UIDs (mixed strings / numbers OK)
 *   folder: source folder, sent verbatim to the API
 */
export function queueBodyPrefetch(slot, uids, folder) {
  if (uids == null) return;
  for (const raw of uids) {
    const uid = Number(raw);
    if (!Number.isFinite(uid)) continue;
    const key = `${slot}:${uid}`;
    if (seen.has(key)) continue;
    seen.add(key);
    queue.push({ slot, uid, folder });
  }
  pump();
}

function canRun() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
  return network.isOnline();
}

function pump() {
  if (active >= MAX_CONCURRENT || queue.length === 0) return;
  if (!canRun()) return;
  // Yield to recent interactive activity: if the user navigated/opened a
  // message in the last ~2.5s, wait before doing prefetch IMAP work.
  const sinceInteractive = Date.now() - lastInteractiveAt;
  if (sinceInteractive < 2500) {
    setTimeout(pump, 2500 - sinceInteractive);
    return;
  }
  const task = queue.shift();
  active += 1;
  runOne(task).finally(() => {
    active -= 1;
    // Trickle: pause between fetches so the IMAP connection stays free for
    // the user's clicks.
    setTimeout(pump, PREFETCH_DELAY_MS);
  });
}

async function runOne({ slot, uid, folder }) {
  try {
    const existing = await getCachedBody(slot, folder, uid);
    if (existing) return;
    const body = await api.get(`/v1/messages/${uid}?folder=${encodeURIComponent(folder)}&render=html`);
    if (body) await putCachedBody(slot, folder, body);
  } catch {
    // Network or 404 - drop the task and let the rest of the queue drain.
  }
}

// Auto-resume on state changes. Both listeners are idempotent and cheap.
network.subscribe((s) => { if (s === "online") pump(); });
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") pump();
  });
}

// Pull uids out of either a flat list item ({uid}) or a threaded item
// ({uids: [...]}). Shared with messageList so both prefetch paths agree.
export function uidsFromItems(items) {
  const out = [];
  if (!items) return out;
  for (const item of items) {
    if (Array.isArray(item.uids)) out.push(...item.uids);
    else if (item.uid != null) out.push(item.uid);
  }
  return out;
}
